import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import type * as React from 'react';
import { cn } from './cn';

/**
 * Button primitive.
 *
 * Variants and sizes follow the shadcn convention FleetTab uses, so the two
 * products share a vocabulary; every colour comes from Routly's own tokens.
 * `asChild` renders the styles onto a child, which is how a router `<Link>`
 * gets button styling without nesting an anchor inside a button.
 */
const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full font-medium ' +
    'select-none transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 ' +
    'focus-visible:outline-ring disabled:pointer-events-none disabled:opacity-50 ' +
    '[&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
  {
    variants: {
      /*
       * FleetSight's pill: a near-black primary, a white outline, a ghost that
       * only shows on hover, and the accent for the one action that should
       * stand out from the primary.
       */
      variant: {
        default: 'bg-primary text-primary-foreground hover:opacity-90',
        destructive: 'bg-destructive text-white hover:opacity-90',
        outline: 'border border-border bg-card text-foreground hover:bg-muted',
        secondary: 'bg-muted text-foreground hover:bg-secondary',
        ghost: 'text-muted-foreground hover:bg-muted hover:text-foreground',
        link: 'text-accent underline-offset-4 hover:underline',
        accent: 'bg-accent text-white hover:opacity-90',
      },
      /*
       * The heights come from the one control ladder in `globals.css` —
       * 28/32/36px — rather than from this file. They were written here as
       * `h-7`/`h-8`/`h-9` against a 0.23rem grid, which is 26/29/33px, and
       * Input and Select were on shadcn's `h-10` at the same time: five
       * different control heights on the fleet overview alone. They are all
       * one ladder now, so a button and the field beside it cannot drift.
       *
       * The floor for a finger is not lost: `base.css` grows every button to
       * 44px under `pointer: coarse`, which is where that rule belongs — it is
       * about the input device, not about the size the interface is drawn at.
       */
      size: {
        default: 'h-control px-4 text-base',
        sm: 'h-control-sm gap-1.5 px-3 text-sm',
        lg: 'h-control-lg px-5 text-base',
        icon: 'size-control-sm',
      },
    },
    /*
     * A destructive icon among ghost icons is a red glyph, not a red disc: in a
     * row of actions the filled circle was the loudest thing on the screen for
     * the one action nobody should reach for by accident.
     */
    compoundVariants: [
      {
        variant: 'destructive',
        size: 'icon',
        className: 'bg-transparent text-error hover:bg-error/10 hover:opacity-100',
      },
    ],
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  ref?: React.Ref<HTMLButtonElement>;
}

/**
 * No `forwardRef`: since React 19 a ref is an ordinary prop. It is declared on
 * the props type because `ButtonHTMLAttributes` does not carry one.
 */
function Button({ className, variant, size, asChild = false, ...props }: ButtonProps) {
  const Comp = asChild ? Slot : 'button';
  return <Comp className={cn(buttonVariants({ variant, size, className }))} {...props} />;
}

export { Button, buttonVariants };
